/**
 * What a report says — the author's content, as the author and the team both read it (ADR 9071):
 * the requirement, the example, the screens (by their navigation names), the markets, the runs, the
 * figures expected, the acceptance criteria and the steps. A field the kind does not ask for is not
 * drawn; one it asks for and the report does not carry says so in words (ADR 9061) — a report filed
 * before the structured fields carries none of them, and that is shown, not hidden.
 *
 * Every value is the author's text, drawn as text and marked `translate="no"`.
 */

import { useTranslation } from 'react-i18next';

import type { FeedbackKind, ReportStructure } from '../../feedback/api';
import { FIELDS_OF_KIND, type RubricField } from '../../feedback/rubric';
import { useSectionLabel } from '../../feedback/words';

export interface ReportContentSource extends ReportStructure {
  readonly kind: FeedbackKind;
  readonly description: string;
  readonly steps: string | null;
}

export function ContentRow({ labelKey, children }: { readonly labelKey: string; readonly children: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="border-hairline grid gap-1 border-b py-2 sm:grid-cols-[9rem_1fr] sm:gap-4">
      <dt className="text-label text-ink-secondary">{t(labelKey)}</dt>
      <dd className="text-body text-ink-primary min-w-0 break-words">{children}</dd>
    </div>
  );
}

/** The rows, without the `<dl>`: the triage drawer adds its own around them. */
export function ReportContentRows({ report }: { readonly report: ReportContentSource }): React.JSX.Element {
  const { t } = useTranslation();
  const sectionLabel = useSectionLabel();
  const asks = (field: RubricField): boolean => (FIELDS_OF_KIND[report.kind] ?? FIELDS_OF_KIND.incident).has(field);
  const none = <span className="text-ink-secondary">{t('feedback.detail.notGiven')}</span>;
  const text = (value: string | null | undefined): React.ReactNode => (
    value === undefined || value === null || value.trim() === ''
      ? none
      : <span translate="no" className="whitespace-pre-wrap">{value}</span>
  );
  const items = (values: readonly string[] | undefined, words: (value: string) => string = (value) => value): React.ReactNode => (
    values === undefined || values.length === 0 ? none : <span translate="no">{values.map(words).join(', ')}</span>
  );
  const criteria = report.acceptance_criteria ?? [];
  const expected = report.expected_figures ?? null;
  const notApplicable = report.expected_figures_not_applicable_reason ?? null;
  return (
    <>
      <ContentRow labelKey="feedback.field.requirement">{text(report.description)}</ContentRow>
      {asks('examples') && <ContentRow labelKey="feedback.field.examples">{text(report.examples)}</ContentRow>}
      {asks('screens') && <ContentRow labelKey="feedback.field.screens">{items(report.affected_sections, sectionLabel)}</ContentRow>}
      {asks('markets') && <ContentRow labelKey="feedback.field.markets">{items(report.markets)}</ContentRow>}
      {asks('runs') && <ContentRow labelKey="feedback.field.runs">{items(report.affected_runs)}</ContentRow>}
      {asks('expected_figures') && (
        <ContentRow labelKey="feedback.field.expectedFigures">
          {(expected === null || expected.trim() === '') && notApplicable !== null && notApplicable.trim() !== ''
            ? t('optima.intake.notApplicable', { reason: notApplicable })
            : text(expected)}
        </ContentRow>
      )}
      {asks('acceptance_criteria') && (
        <ContentRow labelKey="feedback.field.acceptanceCriteria">
          {criteria.length === 0 ? none : (
            <ol className="list-decimal ps-4" translate="no">
              {criteria.map((criterion, index) => <li key={index}>{criterion}</li>)}
            </ol>
          )}
        </ContentRow>
      )}
      {asks('steps') && <ContentRow labelKey="feedback.field.steps">{text(report.steps)}</ContentRow>}
    </>
  );
}
